import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGoUp, useWorkspaceNavigate } from '../../workspace-path';
import {
  clientSchema,
  contractSchema,
  contractWorklogDaySchema,
  metafieldSchema,
  occurrences,
  parseDuration,
  toWireDate,
  lineItemSchema,
  projectSchema,
  taskSchema,
  type Contract,
  type ContractWorklogDay,
  type Metafield,
  type LineItem,
  type Project,
  type Task,
  currencySettingsSchema,
  externalCostSchema,
  lineItemEconomicsSchema,
  ruleVocabularySchema,
  lineItemTargetOptionSchema,
  type LineItemTargetOption,
  type RuleVocabulary,
  type ExternalCost,
  formatMoney,
  type Invoice,
  type Occurrence,
  type LineItemEconomics,
  occurrenceEconomicsSchema,
  type OccurrenceEconomics,
  type SaveLineItemMoneyRequest,
  projectMemberSchema,
  type ProjectMember,
  taskStatusSchema,
  taskPrioritySchema,
  workspaceMemberSchema,
} from '@forecastify/shared';
import { useDuration } from '../../duration';
import { useT } from '../../i18n';
import { CONTRACT_TOPICS } from '../../live/topics';
import { baseCurrencyOf } from '../base-currency';
import { useFirstLoad } from '../use-first-load';
import { useLiveInvalidation } from '../../live/use-live';
import { Toaster, nextSurface, type Toast } from '@forecastify/ui';
import { useExport } from '../exports/use-export';
import { useSession } from '../../session';
import { attachToInvoice } from './invoice-attach';
import { parseList, usePermissionSet } from '../permissions-hook';
import { readAll, requestAll } from '../read-all';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { ContractsScreen, contractErrorKey } from './contracts-screen';
import { bulkContractStatus } from './bulk-status';
import { LineItemSurface, type LineItemMode } from './line-item-surface';
import { ContractSurface, type ContractMode } from './contract-surface';
import { PeriodSurface, type PeriodMode } from './period-surface';
import { usePeriodLogs } from './use-period-logs';
import { useLineItemTasks } from './use-line-item-tasks';
import { ContractDialog, draftRecurrence, type ContractDraft } from './contract-dialog';
import { LineItemDialog, type LineItemDraft } from './line-item-dialog';
import { InvoiceDialog, emptyDraft as emptyInvoice, type InvoiceDraft } from './invoice-dialog';
import {
  ExternalCostDialog,
  emptyDraft as emptyCost,
  type ExternalCostDraft,
} from './external-cost-dialog';
import { useInvoices } from './use-invoices';
import { ownerOrLineItemRefusal } from './project-owners';
import { contractLabel, contractMatches, lineItemContractLabel } from './contract-label';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C24 · `/contracts` and `/contracts/:code`. The route fetches.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A 403 `feature_not_enabled` IS NOT AN ERROR SCREEN HERE, AND THE ROUTE STILL RESOLVES.**
 * `routes.tsx` once declared `feature: contracts.view`, so the sidebar row was gone for a tenant without
 * the plan — but the address has to keep working, because a route that stops existing falls through
 * to the catch-all, which is one of the four failures CLAUDE.md §4 names. Somebody who bookmarked
 * it, or whose plan changed this morning, gets an empty list rather than *"nincs ilyen oldal"*.
 *
 * ⚠️ **Live, on `CONTRACT_TOPICS`.** Every number on this screen is a join through tasks and
 * worklogs, so a colleague logging an hour changes a row here — see `topics.ts`.
 */
export function ContractsRoute({
  page = false,
  itemPage = false,
  periodPage = false,
}: {
  readonly page?: boolean;
  readonly itemPage?: boolean;
  readonly periodPage?: boolean;
} = {}) {
  const { unit: durationUnit } = useDuration();
  const t = useT();
  const session = useSession();
  /* ⚠️ A period's export goes through the same dialog every other export does. It used to POST on
     its own and drop the answer, so the CSV came back and nobody saved it. */
  const exporter = useExport();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  /* ⚠️ The full pages' back arrows go UP — `useGoUp` says why (R-61, 3 Nov 2026). */
  const goUp = useGoUp();
  const params = useParams();
  /* P2-32 · the project gate's *Új sortétel* lands here as `?newLineItem=1` — see the effect. */
  const [search, setSearch] = useSearchParams();
  const permissions = usePermissionSet();
  const sidebar = useSidebarContext();

  /**
   * 31 Oct 2026 · ⚠️⚠️ **TWO SOURCES, ONE NAME.** At an ADDRESS (`/contracts/:code`, a line item, a
   * period) the route resolves the code against every contract the caller can see — `requestAll`,
   * bounded by the workspace. On the REGISTER it reads no contracts of its own: the list pages on
   * the server (`ContractList`), and the page on screen is what its drawer and bulk change find
   * contracts in. A drawer only opens on a row somebody clicked, which is on that page.
   */
  const addressed = page || itemPage || periodPage;
  const [allContracts, setAllContracts] = useState<readonly Contract[]>([]);
  const [listed, setListed] = useState<readonly Contract[]>([]);
  const contracts = addressed ? allContracts : listed;
  const [lineItems, setLineItems] = useState<readonly LineItem[]>([]);
  /* C29 · every worklog under the open contract — the periods table files them by date. */
  const [worklogs, setWorklogs] = useState<readonly ContractWorklogDay[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  /**
   * C56 · ⚠️⚠️ **THE CODE AND ITS DETAILS, BECAUSE ONE REFUSAL IS AN INSTRUCTION.**
   * `contract_still_bound` answers with how many tasks, how many hours and which project — the
   * three things the reader needs to act. A code alone would render *"work still ties this
   * contract to its project"* and leave them hunting for how much.
   *
   * ⚠️ Every other code ignores `values` entirely, so nothing else had to change.
   */
  const [serverError, setServerError] = useState<{
    code: string;
    values: Record<string, string>;
  } | null>(null);
  /** ⚠️ Bumped by a live change and by every write — see the open item's task read. */
  const [liveNonce, setLiveNonce] = useState(0);

  /**
   * Which contract is open, and in which frame.
   *
   * ⚠️ **The PAGE route seeds this from the address and the list route does not**, which is what
   * makes `/contracts/HSO-C01` a link somebody can send. `openCode` is the mockup's own `csel`.
   */
  const [openCode, setOpenCode] = useState<string | null>(null);
  /* ⚠️ Item 34's ladder — a drill-in from a list opens as a modal. `nextSurface` carries it. */
  const [mode, setMode] = useState<ContractMode>(nextSurface('page'));
  /* C25 · the list's two modes (the mockup's `cMode`), and which line item is open. */
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [itemMode, setItemMode] = useState<LineItemMode>(nextSurface('page'));
  /* C29 · which period is open, by its START DATE — P2-14. */
  const [openPeriod, setOpenPeriod] = useState<string | null>(null);
  const [periodMode, setPeriodMode] = useState<PeriodMode>(nextSurface('page'));
  /**
   * 30 Oct 2026 · the collector tasks of the contracts on screen (P2-10) — asked for by
   * `recurringContractId` rather than found in one page of the workspace's tasks, which missed a
   * collector whenever it was not among the newest fifty.
   */
  const [tasks, setTasks] = useState<readonly Task[]>([]);
  /* 30 Oct 2026 · the rule editor's pickers, each from its own list rather than from task rows. */
  const [ruleStatuses, setRuleStatuses] = useState<
    readonly { id: string; name: string; color: string }[]
  >([]);
  const [rulePriorities, setRulePriorities] = useState<readonly { id: string; name: string }[]>([]);
  const [members, setMembers] = useState<readonly { id: string; name: string }[]>([]);

  /* C24bis · the two create/edit dialogs, and the projects the contract one picks from. */
  const [projects, setProjects] = useState<readonly Project[]>([]);
  /* C56 · the clients a contract may hang off when it has no project. */
  const [clients, setClients] = useState<readonly { id: string; name: string }[]>([]);
  /* C30 · the workspace's own fields for the two entities. Empty for most workspaces. */
  const [contractFields, setContractFields] = useState<readonly Metafield[]>([]);
  const [itemFields, setItemFields] = useState<readonly Metafield[]>([]);
  /**
   * C55 · the open line item's finance block.
   *
   * ⚠️⚠️ **ITS OWN READ, AND NON-FATAL.** The figures are a join over every worklog, every rate
   * card period and the currency matrix — so carrying them on the line item would put that cost on
   * every LIST of line items, to draw a card only the detail shows. `null` is *not loaded* and the
   * two cards simply do not appear, which is the same rule the tasks tab already follows.
   */
  const [economics, setEconomics] = useState<LineItemEconomics | null>(null);
  /** `0160` · the open line item's Jira values, for the rule editor's three remote fields. */
  const [ruleVocabulary, setRuleVocabulary] = useState<RuleVocabulary | null>(null);
  /** `0161` · what a line item may target, and the answer a *Szinkronizálás most* press gets. */
  const [targetOptions, setTargetOptions] = useState<readonly LineItemTargetOption[]>([]);
  /** OS-1 · the open line item's project members — who a time-and-material line item may name. */
  const [projectMembers, setProjectMembers] = useState<readonly ProjectMember[]>([]);
  const [syncToasts, setSyncToasts] = useState<readonly Toast[]>([]);
  /* C56 · the costs on the open line item. Read beside its economics and just as non-fatally. */
  const [itemCosts, setItemCosts] = useState<readonly ExternalCost[]>([]);
  const [currencies, setCurrencies] = useState<
    readonly { code: string; symbol: string; rateToBase: string; isDefault: boolean }[]
  >([]);
  /**
   * C57 · ⚠️ **The whole workspace's invoices, filtered per surface.** A line item's are a handful
   * and a period's are counted the mockup's way — the occurrence arm PLUS the contract's line-item
   * invoices dated inside it — so a per-surface fetch would be two endpoints answering a question
   * one list already contains.
   */
  const invoiceData = useInvoices({
    /* 31 Oct 2026 · the open line item's or the open contract's — both arms of a period's
       invoices are its contract's (`?contractId=`), and nothing else here shows invoices. */
    contractId:
      lineItems.find((item) => item.id === openItemId)?.contractId ??
      contracts.find((candidate) => contractMatches(candidate, openCode ?? params.code))?.id ??
      null,
  });
  const [billing, setBilling] = useState<InvoiceDraft | null>(null);
  /** An external cost being recorded from a line item, with its link already set. */
  const [costDraft, setCostDraft] = useState<ExternalCostDraft | null>(null);
  const [costError, setCostError] = useState<string | null>(null);
  /* C55/C57 · the open period's — or open recurring contract's — series, priced per period.
     ⚠️ Non-fatal: `[]` is *not loaded*. */
  const [periodMoney, setPeriodMoney] = useState<readonly OccurrenceEconomics[]>([]);
  /* 17 Oct 2026 · the open contract's line items, priced — the *Költés* column. */
  const [lineItemMoney, setLineItemMoney] = useState<readonly LineItemEconomics[]>([]);

  const [newOpen, setNewOpen] = useState(false);
  /**
   * C24bis · whether the create dialog is open.
   *
   * ⚠️ **A boolean since feedback round 4 item 7**, where it was `{ editing: LineItem | null }`.
   * The dialog no longer edits — the line item's own page does — so there is nothing left for it
   * to carry.
   */
  const [itemDialogOpen, setItemDialogOpen] = useState(false);

  useEffect(() => {
    if (!page) return;
    setOpenCode(params.code ?? null);
    setMode('full');
  }, [page, params.code]);

  /**
   * ⚠️ **R-57 · `/contracts/cont_12a3cd5a/li_0bb16eb3` — both identifiers.** The older shape,
   * `/contracts/HSO-C01/2` (the code, then the ordinal within the contract), is still accepted, so
   * an agent's sentence, a mail and a bookmark keep opening the right page. The effect runs on the
   * lists rather than only on the params, because on a cold load the address is known before the
   * data is.
   */
  useEffect(() => {
    if (!itemPage) return;
    const contract = contracts.find((candidate) => contractMatches(candidate, params.code));
    if (contract === undefined) return;
    const item = lineItems.find(
      (candidate) =>
        candidate.contractId === contract.id &&
        (candidate.identifier === params.number || candidate.number === Number(params.number)),
    );
    setOpenItemId(item?.id ?? null);
    setItemMode('full');
  }, [itemPage, params.code, params.number, contracts, lineItems]);

  /* R-57 · an address that resolved by an old name (a code, an ordinal) is rewritten in place to
     the identifier form, so what the reader copies from the bar is the canonical link. */
  useEffect(() => {
    if (!page && !itemPage && !periodPage) return;
    const contract = contracts.find((candidate) => contractMatches(candidate, params.code));
    if (contract === undefined) return;
    let canonical: string;
    if (itemPage) {
      const item = lineItems.find(
        (candidate) =>
          candidate.contractId === contract.id &&
          (candidate.identifier === params.number || candidate.number === Number(params.number)),
      );
      if (item === undefined) return;
      canonical = `/contracts/${contract.identifier}/${item.identifier}`;
      if (params.code === contract.identifier && params.number === item.identifier) return;
    } else if (periodPage) {
      if (params.code === contract.identifier) return;
      canonical = `/contracts/${contract.identifier}/periods/${params.from ?? ''}`;
    } else {
      if (params.code === contract.identifier) return;
      canonical = `/contracts/${contract.identifier}`;
    }
    const query = search.toString();
    go(query === '' ? canonical : `${canonical}?${query}`, { replace: true });
  }, [
    page,
    itemPage,
    periodPage,
    params.code,
    params.number,
    params.from,
    contracts,
    lineItems,
    search,
    go,
  ]);

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }

    /* 31 Oct 2026 · the register reads its own page — `addressed` says why. A probe of one row
       stands in for it here, so a refusal is still this screen's error state rather than an
       empty table, and the plan boundary still reads as empty. */
    const [contractList, itemList] = await Promise.all([
      (addressed ? requestAll(session, '/contracts') : session.request('/contracts?limit=1')).catch(
        () => null,
      ),
      session.request('/line-items').catch(() => null),
    ]);

    if (contractList === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }

    /* ⚠️ The plan boundary is an EMPTY list, not a failure — see the note on this component. */
    if (contractList.status === 403) {
      setAllContracts([]);
      setLineItems([]);
      setFailure(null);
      return;
    }

    if (!contractList.ok) {
      setFailure(await failureOf(contractList));
      return;
    }

    if (addressed) setAllContracts(parseList(contractSchema, await contractList.json()) ?? []);
    /* ⚠️ **Non-fatal.** A caller who can read contracts but whose line-item read fails should still
     * get the list; an empty items array renders a detail whose table says so, which is honest.
     * Making it fatal would take down the screen over a card nobody opened. */
    if (itemList?.ok === true) {
      setLineItems(parseList(lineItemSchema, await itemList.json()) ?? []);
    }
    setFailure(null);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId, addressed]);

  /* First load — `use-first-load.ts`. The register's skeleton rows are its own list's. */
  useFirstLoad(reload);

  /**
   * ⚠️ **`liveNonce` rides along with the reload**, so a read that is not part of `reload` — the
   * open line item's tasks — hears about a change too. Depending on `lineItems` instead worked
   * only when the line items themselves came back different, which a task moving between them does
   * not guarantee.
   */
  useLiveInvalidation(
    CONTRACT_TOPICS,
    () => {
      void reload();
      setLiveNonce((current) => current + 1);
    },
    /* 30 Oct 2026 · a running Jira sync is a stream of `task` events; at most every five seconds,
       and once more when it stops — not once a second for as long as it runs (UAT). */
    { steadyMs: 5000 },
  );

  /**
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ 30 Oct 2026 · THE PICKERS FROM THEIR OWN LISTS, NOT FROM A PAGE OF TASKS.         │
   * └──────────────────────────────────────────────────────────────────────┘
   *
   * The rule editor's statuses, priorities and assignees used to be whatever appeared on the
   * workspace's newest fifty tasks — so *assignee is* offered only the people who happened to own
   * one of them (UAT, 29 Oct). Every member, every status and every priority instead.
   *
   * ⚠️ **Non-fatal**: a refusal leaves the picker empty, and the rest of the screen works.
   */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const [statusList, priorityList, memberList] = await Promise.all([
        session.request('/task-statuses').catch(() => null),
        session.request('/task-priorities').catch(() => null),
        readAll(session, '/members', workspaceMemberSchema).catch(() => null),
      ]);
      if (statusList?.ok === true) {
        const list = parseList(taskStatusSchema, await statusList.json());
        if (list !== null) {
          setRuleStatuses(list.map(({ id, name, color }) => ({ id, name, color })));
        }
      }
      if (priorityList?.ok === true) {
        const list = parseList(taskPrioritySchema, await priorityList.json());
        if (list !== null) setRulePriorities(list.map(({ id, name }) => ({ id, name })));
      }
      if (memberList !== null) {
        setMembers(memberList.map((member) => ({ id: member.id, name: member.displayName })));
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /**
   * The open line item's project, in full — both sets the detail needs, in one request.
   *
   * ⚠️ **Per line item, which the screen-wide read above deliberately is not**, and the reason it
   * gave has held: the detail needs *what is assigned here* AND *what could be*, and asking the
   * workspace for both once was cheaper than asking per item. What it missed is that the answer
   * has to be COMPLETE, and one page of a workspace's tasks is not. A project is the narrowest
   * question that still answers both, because the server refuses a cross-project assignment
   * anyway (`line_item_not_on_project`) — so the picker could never have offered more than this.
   *
   * ⚠️⚠️ **EVERY PAGE OF IT (30 Oct 2026).** One page of 200 was read here, and a project past it
   * clipped the table as well as the picker: CARNET's *0. fázis* said *3 by rule* over an empty
   * list, because its three tasks were outside the project's first 200 of 308 (UAT).
   */
  /* ⚠️ Resolved from `lineItems` rather than from `openItem`, which this file derives further
     down — a hook may not read a `const` declared below it. */
  const openItemProjectId = lineItems.find((item) => item.id === openItemId)?.projectId ?? null;

  /**
   * C30 · the workspace's metafields for `contract` and `lineitem`.
   *
   * ⚠️ **One request, filtered here.** `GET /metafields` returns the workspace's whole set — a few
   * dozen rows at most — and two narrowed requests would be two round trips for one screen.
   *
   * ⚠️ **Non-fatal**: a caller who cannot read them gets the details without the card, which is
   * what a workspace with no fields sees anyway.
   */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/metafields').catch(() => null);
      if (response?.ok !== true) return;
      const all = parseList(metafieldSchema, await response.json()) ?? [];
      setContractFields(all.filter((field) => field.entity === 'contract'));
      setItemFields(all.filter((field) => field.entity === 'lineitem'));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /**
   * The projects a contract can hang off.
   *
   * ⚠️ **Read once, and non-fatal like the rest.** Somebody who cannot list projects cannot pick
   * one, so the create dialog opens with an empty picker and refuses — which is the same answer the
   * server would give, arrived at without a round trip.
   */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      /* 30 Oct 2026 · every page — one page of 200 drops projects past it from the picker. */
      const rows = await readAll(session, '/projects', projectSchema).catch(() => null);
      if (rows !== null) setProjects(rows);
    })();
    void (async () => {
      /* ⚠️ Its own read, and a refusal is `[]` rather than a screen failure: a member who may
         not see clients simply cannot pick the `client` scope, and the rest of the page works. */
      const parsed = await readAll(session, '/clients', clientSchema).catch(() => null);
      if (parsed !== null) {
        setClients(parsed.map((one) => ({ id: one.id, name: one.name })));
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  /**
   * The open contract's worklogs, for the periods table.
   *
   * ⚠️ **Only when something is open, and only when it RECURS.** A milestone contract has no
   * periods, so its hours are a request nobody looks at — and this is the largest read on the
   * screen.
   *
   * ⚠️ **Non-fatal**: the periods table says zero rather than the screen failing.
   */
  useEffect(() => {
    const contract = contracts.find((candidate) =>
      contractMatches(candidate, openCode ?? params.code),
    );
    if (contract?.recurrence == null) {
      setWorklogs([]);
      return;
    }
    void (async () => {
      /* 31 Oct 2026 · hours per DAY — bounded by the calendar, where every worklog stopped at
         5,000 without saying so. The period detail pages its own logs (`use-period-logs.ts`). */
      const response = await session
        .request(`/contracts/${contract.id}/worklogs/days`)
        .catch(() => null);
      if (response?.ok !== true) {
        setWorklogs([]);
        return;
      }
      setWorklogs(parseList(contractWorklogDaySchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [openCode, params.code, contracts, liveNonce]);

  /**
   * One write, its refusal turned into a sentence, then a reload.
   *
   * ⚠️ **Resolves to whether it WORKED**, because the two dialogs close on success and must stay
   * open on refusal — with the sentence in them. A void write closes on a 409 too, which loses both
   * the message and everything the reader typed.
   */
  const write = async (path: string, init: RequestInit): Promise<boolean> => {
    setServerError(null);
    const response = await session.request(path, init).catch(() => null);
    if (response?.ok !== true) {
      const envelope = (await response?.json().catch(() => null)) as
        | {
            error?: {
              code?: string;
              details?: { field?: string; message?: string }[];
            };
          }
        | null
        | undefined;
      const code = envelope?.error?.code ?? 'unavailable';
      /* ⚠️ The envelope's `details` are `{ field, message }` pairs — rule 5's shape — which is
         exactly what an interpolated string wants, keyed by the field. */
      const values = Object.fromEntries(
        (envelope?.error?.details ?? []).flatMap((detail) =>
          detail.field === undefined || detail.message === undefined
            ? []
            : [[detail.field, detail.message]],
        ),
      );
      setServerError({ code, values });
      return false;
    }
    await reload();
    /* ⚠️ A rule saved here re-evaluates the whole project server-side, and what MOVED is a task
       rather than a line item — so the open item's task list has to be told as well. */
    setLiveNonce((current) => current + 1);
    /* The sidebar's contract count is a different read, and it counts ACTIVE ones — so a status
     * change moves it. Without this the row keeps yesterday's number until a navigation. */
    sidebar?.reload();
    return true;
  };

  const open = contracts.find((contract) => contractMatches(contract, openCode)) ?? null;

  /**
   * P2-32 · ⚠️ **`?newLineItem=1` opens the line item dialog on the contract it names**, the same
   * `?new=1` convention the command palette uses — the project gate's one action, for a project that
   * has a contract and nothing on it. Waits for the contract to load, and is cleared with `replace`
   * so Back and a cancel do not reopen it.
   */
  const newLineItemRequested = search.get('newLineItem') !== null;
  useEffect(() => {
    if (!newLineItemRequested || open === null) return;
    if (open.canManageLineItems) setItemDialogOpen(true);
    const next = new URLSearchParams(search);
    next.delete('newLineItem');
    setSearch(next, { replace: true });
  }, [newLineItemRequested, open, permissions, search, setSearch]);

  /**
   * ⚠️⚠️ **THE OWNER IS THE PERSON FILLING IN THE FORM, because the mockup's dialog has no owner
   * field and `POST /contracts` requires one.** `newContract` opens with `owner: ME` and never
   * asks; the contract detail is where it changes hands. If this browser has no member id — which
   * means it has no workspace — the create is not offered at all.
   */
  const createContract = (draft: ContractDraft): void => {
    const memberId = session.workspaces.find(
      (workspace) => workspace.id === session.workspaceId,
    )?.memberId;
    if (memberId === undefined) return;

    void write('/contracts', {
      method: 'POST',
      body: JSON.stringify({
        name: draft.name.trim(),
        /* ⚠️⚠️ C56 · **ONE OF THE TWO, NEVER BOTH.** The scope is the dialog's control and
           the row has no such column; what the server receives is a project or a client, and
           `createContractRequestSchema` refuses a body carrying neither. */
        ...(draft.scope === 'client'
          ? { clientId: draft.clientId }
          : { projectId: draft.projectId }),
        type: draft.type,
        startsOn: draft.startsOn,
        endsOn: draft.endsOn === '' ? null : draft.endsOn,
        ownerMemberId: memberId,
        /* ⚠️ Sent only when the workspace HAS fields — `{}` clears every value, so an absent map is
           what "this workspace defines none" has to look like. */
        ...(Object.keys(draft.metafields).length === 0 ? {} : { metafields: draft.metafields }),
        /* ⚠️ **A recurring contract cannot be created without a recurrence** —
           `contracts_recurrence_shape` refuses it — so the dialog's monthly default travels here
           and the detail's own card refines it. `endMode: 'date'` with a null date is the
           open-ended case, which is why there is no third mode. */
        recurrence: draft.type === 'recurring' ? draftRecurrence(draft) : null,
      }),
    }).then((ok) => {
      if (ok) setNewOpen(false);
    });
  };

  /**
   * One handler for both halves of the line-item dialog.
   *
   * ⚠️ **`estimateHours` goes out as a NUMBER**, parsed by the shared helper — the request schema
   * is `z.number()`, and an empty box means zero rather than "leave it", which on create is the
   * column's default and on edit is a deliberate reset to nothing.
   */
  const saveLineItem = (draft: LineItemDraft): void => {
    if (open === null) return;
    void write('/line-items', {
      method: 'POST',
      body: JSON.stringify({
        contractId: open.id,
        name: draft.name.trim(),
        /* ⚠️ C55 · a licence has no estimate and `0151` refuses one — so the form hides the
           field and this sends the zero rather than whatever a previous type left in the draft. */
        estimateHours:
          draft.type === 'license'
            ? 0
            : draft.estimateHours.trim() === ''
              ? 0
              : /* DU-2 · in the unit the dialog's picker named. */
                (parseDuration(draft.estimateHours, draft.estimateUnit ?? durationUnit) ?? 0),
        startsOn: draft.startsOn === '' ? null : draft.startsOn,
        endsOn: draft.endsOn === '' ? null : draft.endsOn,
        /* ⚠️ Omitted while unchosen — off the pricing tier the dialog does not draw the field, and
           the column's own default (`non_billable`) is what a missing type means. */
        ...(draft.type === '' ? {} : { type: draft.type }),
        /**
         * ⚠️⚠️ **ROUND 5 ITEM 19 — AND AN EMPTY BOX SENDS NOTHING, NOT A ZERO.** The three keys
         * are optional on the request schema, so omitting one leaves the column null: *no budget
         * agreed*. Spreading a `0` instead would record that somebody agreed on nothing, which the
         * money card then draws as `0 Ft` and the margin arithmetic believes.
         */
        ...(draft.budget.trim() === '' ? {} : { budget: Number(draft.budget.replace(',', '.')) }),
        ...(draft.revenue.trim() === ''
          ? {}
          : { revenue: Number(draft.revenue.replace(',', '.')) }),
        ...(draft.currencyCode === '' ? {} : { currencyCode: draft.currencyCode }),
        ...(Object.keys(draft.metafields).length === 0 ? {} : { metafields: draft.metafields }),
      }),
    }).then((ok) => {
      if (ok) setItemDialogOpen(false);
    });
  };

  const close = (): void => {
    setOpenCode(null);
    /* ⚠️ The PAGE's arrow goes back to where the contract was opened from — the project's
     * Szerződések tab, the register — and otherwise UP to its project's tab, or the register for
     * a contract with no project (R-61, amended 3 Nov 2026). */
    if (page) {
      goUp(open?.projectId == null ? '/contracts' : `/projects/${open.projectId}#contracts`, {
        followOrigin: true,
      });
    }
  };

  /* ⚠️ Bumped after a money write so the block re-reads — the figures are derived, so a local
     patch would have to re-run the cost join in the browser. */
  const [moneyNonce, setMoneyNonce] = useState(0);
  const openItem = lineItems.find((item) => item.id === openItemId) ?? null;
  /**
   * 31 Oct 2026 · the open line item's tasks, paged on the server — `use-line-item-tasks.ts`.
   *
   * ⚠️ It replaced a read of EVERY task of the line item's project (feedback round 4 item 8 had
   * moved it there from one page of the workspace's). `liveNonce` still carries a task joining by
   * rule — `CONTRACT_TOPICS` includes `task` — through to the page.
   */
  const lineItemTasks = useLineItemTasks(session, openItem, liveNonce);
  /**
   * C26 · what a rule condition's value picker offers.
   *
   * ⚠️ **Derived from what the route already holds**, not fetched: projects come with the create
   * dialog, and statuses, priorities and assignees are on every task row. A rule editor that made
   * four more requests to fill four dropdowns would pay for them on a screen most people open to
   * read rather than to edit.
   */
  /** ⚠️ The same list the rule's assignee picker offers — every member of the workspace. */
  const metafieldMembers = members;

  /**
   * `0160` · what the open line item's project has synced from Jira, for the rule's Jira fields.
   *
   * ⚠️ **Fetched, unlike the four pickers above**, because no row this route holds carries a
   * component or an epic — they live on the issue link, not the task. One request per open, and on
   * `liveNonce`: a sync landing new issues is a `task` event, and a component that just arrived is
   * one somebody may want to pick.
   *
   * ⚠️ Cleared first, so the previous line item's components never sit under this one's rule —
   * but ONLY when the line item changes (30 Oct 2026). Cleared on every live refresh too, the rule
   * chip had nothing to name its value with until the read landed and showed the raw id: during a
   * Jira sync it flickered between *Tervezés* and *14580* once a second (UAT).
   */
  const vocabularyFor = useRef<string | null>(null);
  useEffect(() => {
    if (vocabularyFor.current !== openItemId) {
      vocabularyFor.current = openItemId;
      setRuleVocabulary(null);
    }
    if (openItemId === null || session.workspaceId === null) return;
    void (async () => {
      const response = await session
        .request(`/line-items/${openItemId}/rule-vocabulary`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const parsed = ruleVocabularySchema.safeParse(await response.json());
      if (parsed.success) setRuleVocabulary(parsed.data);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the open item and the live signal are what change. */
  }, [openItemId, session.workspaceId, liveNonce]);

  /**
   * OS-1 · the open line item's PROJECT members, for the people a time-and-material line item names.
   *
   * ⚠️ **The project's, not the task assignees' `metafieldMembers`**, because P-1 is what the server
   * asks — a member at some point inside the line item's window — and a person placed on a project
   * may not have a task yet. Non-fatal and cleared first, like the rule vocabulary above.
   */
  /* 30 Oct 2026 · cleared when the PROJECT changes, not on every live refresh — the vocabulary's reason. */
  const membersFor = useRef<string | null>(null);
  useEffect(() => {
    if (membersFor.current !== openItemProjectId) {
      membersFor.current = openItemProjectId;
      setProjectMembers([]);
    }
    if (openItemProjectId === null || session.workspaceId === null) return;
    void (async () => {
      const response = await session
        .request(`/projects/${openItemProjectId}/members`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = projectMemberSchema.array().safeParse(body?.data);
      if (parsed.success) setProjectMembers(parsed.data);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the open project and the live signal are what change. */
  }, [openItemProjectId, session.workspaceId, liveNonce]);

  /**
   * `0161` · the instances and tracker projects a line item may target.
   *
   * ⚠️ **Only for somebody who may set a target** (`lineitem.manage`): the server gates the read
   * the same way, and without it the section still shows an existing target, read-only.
   */
  const canTarget = permissions.has('lineitem.manage');
  useEffect(() => {
    if (!canTarget || session.workspaceId === null) {
      setTargetOptions([]);
      return;
    }
    void (async () => {
      const response = await session.request('/line-items/target-options').catch(() => null);
      if (response?.ok !== true) return;
      const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
      const parsed = lineItemTargetOptionSchema.array().safeParse(body?.data);
      if (parsed.success) setTargetOptions(parsed.data);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the live signal is what changes. */
  }, [canTarget, session.workspaceId, liveNonce]);

  const ruleOptions = useMemo(
    () => ({
      projects: projects.map((project) => ({
        id: project.id,
        name: project.name,
        color: project.color,
      })),
      statuses: ruleStatuses,
      priorities: rulePriorities,
      members,
      components: ruleVocabulary?.components ?? [],
      fixVersions: ruleVocabulary?.fixVersions ?? [],
      epics: ruleVocabulary?.epics ?? [],
    }),
    [projects, ruleStatuses, rulePriorities, members, ruleVocabulary],
  );

  /**
   * P2-14 · `/contracts/HSO-C01/periods/2026-08-01` — the code, then the period's first day.
   *
   * ⚠️ **A DATE, because a period has no id and its index is a position in a list the recurrence
   * can change.** Edit the step and `#8` is a different fortnight; a date either still starts a
   * period or it does not, and the second case says so rather than silently showing another one.
   */
  useEffect(() => {
    if (!periodPage) return;
    setOpenPeriod(params.from ?? null);
    setPeriodMode('full');
  }, [periodPage, params.from]);

  const itemContract =
    openItem === null
      ? null
      : (contracts.find((candidate) => candidate.id === openItem.contractId) ?? null);

  /* ⚠️ Re-read whenever the OPEN ITEM changes, and cleared first — otherwise the card shows the
     previous line item's money for as long as the request takes, which is the worst possible
     wrong answer: plausible, specific and about something else. */
  useEffect(() => {
    setEconomics(null);
    setItemCosts([]);
    if (openItemId === null || session.workspaceId === null) return;
    void (async () => {
      const response = await session
        .request(`/line-items/${openItemId}/economics`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const parsed = lineItemEconomicsSchema.safeParse(await response.json());
      if (parsed.success) setEconomics(parsed.data);
    })();
    void (async () => {
      /* 31 Oct 2026 · the endpoint pages now; a line item's costs are all of them. */
      const response = await requestAll(session, `/external-costs?lineItemId=${openItemId}`).catch(
        () => null,
      );
      if (response?.ok !== true) return;
      setItemCosts(parseList(externalCostSchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the open item is what actually changes. */
  }, [openItemId, session.workspaceId, moneyNonce]);

  /* ⚠️ The workspace's currencies, for the symbol beside each figure. Read once; a 403 leaves
     the code itself as the symbol, which is legible if unlovely. */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/workspace/currencies').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = currencySettingsSchema.safeParse(await response.json());
      if (parsed.success) {
        setCurrencies(
          parsed.data.currencies.map((one) => ({
            code: one.code,
            symbol: one.symbol,
            rateToBase: one.rateToBase,
            isDefault: one.isDefault,
          })),
        );
      }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- see above. */
  }, [session.workspaceId]);

  /**
   * C25 · the line item detail, in whichever frame.
   *
   * ⚠️ **No page route of its own yet, and that is a gap rather than a decision.** The mockup has
   * one (`isLinePage`), and it needs an address a line item can be named by — its id is a uuid, and
   * the code that would read well (`HSO-C01/Havi támogatás`) is not unique. So `full` falls back to
   * the modal, which is a real frame rather than a dead control, and the address question is
   * written down instead of guessed at.
   */
  const itemSurface =
    openItem === null ? null : (
      <LineItemSurface
        /* ⚠️ R-38 · workspace-relative, so `go` supplies the first segment. */
        onNavigate={(path) => {
          go(path);
        }}
        mode={itemMode}
        open
        data={{
          item: openItem,
          taskList: lineItemTasks,
          contractName: itemContract?.name ?? lineItemContractLabel(openItem),
          projectName: itemContract?.projectName ?? '',
          projectColor: itemContract?.projectColor ?? 'var(--muted-foreground)',
          metafields: itemFields,
          members: metafieldMembers,
          projectMembers,
          economics,
          externalCosts: itemCosts,
          invoices: invoiceData.invoices.filter((one) => one.lineItemId === openItem.id),
          currencies,
          /* ⚠️ Only a RECURRING contract has a frame for a scope to be inside — `liScopeOn`. */
          recurring: itemContract?.type === 'recurring',
          internal: itemContract?.projectInternal ?? false,
          lineItemNames: new Map(lineItems.map((one) => [one.id, one.name])),
          /* C26 · what the rule's value pickers offer. The route already reads projects for the
             contract dialog; statuses, priorities and members come with the tasks it holds. */
          ruleOptions,
          targetOptions,
        }}
        handlers={{
          canManage: openItem.canManage,
          /* ⚠️ `contract.manage`, which is what the server checks on this endpoint — the money is
             a commercial fact about the contract rather than a property of the deliverable. */
          /* 22 Oct 2026 · the server's answer for this item's project (`project-reach.ts`). */
          ...(openItem.canManageMoney
            ? {
                onMoney: (patch: SaveLineItemMoneyRequest) => {
                  void (async () => {
                    const response = await session
                      .request(`/line-items/${openItem.id}/economics`, {
                        method: 'PUT',
                        body: JSON.stringify(patch),
                      })
                      .catch(() => null);
                    if (response?.ok !== true) return;
                    const parsed = lineItemEconomicsSchema.safeParse(await response.json());
                    if (parsed.success) setEconomics(parsed.data);
                    setMoneyNonce((count) => count + 1);
                  })();
                },
              }
            : {}),
          /* R1 · 3 Nov 2026 · nothing new goes on a CLOSED line item, a cost included — the server
             refuses it with `line_item_closed`, so the action is not drawn. */
          ...(openItem.canManageMoney && openItem.projectId !== null && openItem.status !== 'closed'
            ? {
                onNewCost: () => {
                  const projectId = openItem.projectId;
                  if (projectId === null) return;
                  setCostError(null);
                  setCostDraft({
                    ...emptyCost(
                      projectId,
                      economics?.currencyCode ?? baseCurrencyOf(currencies)?.code ?? 'HUF',
                      '',
                    ),
                    linkKind: 'line_item',
                    lineItemId: openItem.id,
                  });
                },
              }
            : {}),
          /* C57 · ⚠️ `contract.manage` again — billing is a commercial act on the agreement, and it
             is the same authority that sets the price the invoice is capped against.
             ⚠️ U-6 · and never on a recurring contract: it is invoiced by period, and the server
             refuses a line-item invoice there with `invoice_by_period`. */
          /* R1 · 3 Nov 2026 · and never on a CLOSED line item: an invoice is something new on it,
             so it is reopened first (the product owner) — the server says `line_item_closed`. */
          ...(openItem.canManageMoney && !openItem.contractRecurring && openItem.status !== 'closed'
            ? {
                onBill: () => {
                  setBilling(
                    /* ⚠️ The remaining revenue, pre-filled — `invBill` in the mockup. Somebody
                       billing the last of a deliverable should not have to retype a figure the
                       screen above them is already showing. */
                    emptyInvoice(
                      economics?.currencyCode ?? baseCurrencyOf(currencies)?.code ?? 'HUF',
                      '',
                      {
                        targetKind: 'line_item',
                        lineItemId: openItem.id,
                        amount: economics?.remaining ?? '',
                        locked: true,
                      },
                    ),
                  );
                },
              }
            : {}),
          onOpenInvoice: (invoice: Invoice) => {
            go(`/invoices/${invoice.identifier}`);
          },
          pricing: sidebar?.view.features['contracts.line_item_pricing'] === true,
          /* ⚠️ `0116` · the line item's own folder. The same shape the contract's uses, so a
             refusal reads identically on both — see the note there. */
          onAttach: async (file) => {
            const form = new FormData();
            form.set('file', file);
            const response = await session
              .request(`/files?target=line_item&targetId=${openItem.id}`, {
                method: 'POST',
                body: form,
              })
              .catch(() => null);
            if (response?.ok === true) {
              await reload();
              return null;
            }
            const envelope = (await response?.json().catch(() => null)) as
              { error?: { code?: string } } | null | undefined;
            return envelope?.error?.code ?? 'unavailable';
          },
          onPatch: (patch) => {
            void write(`/line-items/${openItem.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ ...patch, version: openItem.version }),
            });
          },
          onAssign: (task, itemId) =>
            write(`/tasks/${task.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ lineItemId: itemId, version: task.version }),
            }),
          onOpenContract: () => {
            const contract = contracts.find((candidate) => candidate.id === openItem.contractId);
            if (contract === undefined) return;
            setOpenItemId(null);
            /* ⚠️ C56 · the label, not the code — a client-scoped contract opens by its identifier,
               and `contractMatches` accepts either. Passing the raw code would set null here and
               the drawer would simply never open. */
            setOpenCode(contractLabel(contract));
            /* ⚠️ **From wherever the LINE ITEM is**, not from the page: this handler runs inside
               the line-item surface, so the ladder's question is *"what am I in"*. */
            setMode(nextSurface(itemMode));
          },
          onOpenTask: (task) => {
            go(`/tasks/${task.key}`);
          },
          onOpenLineItem: (lineItemId) => {
            const other = lineItems.find((one) => one.id === lineItemId);
            if (other !== undefined)
              go(`/contracts/${lineItemContractLabel(other)}/${other.identifier}`);
          },
          onOpenProject: () => {
            if (openItem.projectId !== null) go(`/projects/${openItem.projectId}`);
          },
          onRule: (rule) =>
            write(`/line-items/${openItem.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ rule, version: openItem.version }),
            }),
          onMode: (assignment) =>
            write(`/line-items/${openItem.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ assignment, version: openItem.version }),
            }),
          onTarget: (target, rule) =>
            /* ⚠️ D6 · a target collects BY RULE, so setting one also puts the line item in rule
               mode — one patch, so the two cannot disagree for a reload. ⚠️ 4 Nov 2026 · and the
               filters written before it was saved ride in the SAME patch, so the first routing
               pass is the filtered one rather than the whole tracker project. */
            write(`/line-items/${openItem.id}`, {
              method: 'PATCH',
              body: JSON.stringify(
                target === null
                  ? { target: null, version: openItem.version }
                  : {
                      target,
                      assignment: 'rule',
                      ...(rule === undefined ? {} : { rule }),
                      version: openItem.version,
                    },
              ),
            }),
          onSync: () => {
            void (async () => {
              const response = await session
                .request(`/integrations/line-items/${openItem.id}/sync`, { method: 'POST' })
                .catch(() => null);
              const body = (await response?.json().catch(() => null)) as {
                data?: { kind?: string };
              } | null;
              const kind = body?.data?.kind;
              /* ⚠️ `already` is an answer, not a failure — the project button's reasoning. */
              const ok = response?.ok === true && (kind === 'queued' || kind === 'already');
              setSyncToasts((current) => [
                ...current,
                {
                  id: `line-item-sync-${String(Date.now())}`,
                  title: ok
                    ? t(kind === 'already' ? 'projects.sync.already' : 'projects.sync.started')
                    : t('projects.sync.failed'),
                  ...(ok && kind === 'queued'
                    ? { description: t('projects.sync.startedNote') }
                    : {}),
                  variant: ok ? ('success' as const) : ('error' as const),
                },
              ]);
            })();
          },
          onMetafields: (values) => {
            void write(`/line-items/${openItem.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ metafields: values, version: openItem.version }),
            });
          },
          onExclude: (taskId, excluded) =>
            write(`/contracts/line-items/${openItem.id}/exclusions/${taskId}`, {
              method: 'PUT',
              body: JSON.stringify({ excluded }),
            }),
          ...(itemPage
            ? {}
            : {
                onExpand: () => {
                  go(`/contracts/${lineItemContractLabel(openItem)}/${openItem.identifier}`);
                },
              }),
        }}
        onModeChange={(next) => {
          /* ⚠️ Growing to full is a NAVIGATION, so the address matches what is on screen and the
             page is shareable; shrinking comes back to the contract with the drawer still open. */
          if (next === 'full') {
            go(`/contracts/${lineItemContractLabel(openItem)}/${openItem.identifier}`);
            return;
          }
          if (itemPage) {
            go(`/contracts/${lineItemContractLabel(openItem)}`);
            setItemMode(next);
            return;
          }
          setItemMode(next);
        }}
        onClose={() => {
          setOpenItemId(null);
          /* ⚠️ The PAGE's arrow goes UP to its contract (R-61, 3 Nov 2026) — a line item address
             with nothing open would be a blank screen. */
          if (itemPage) goUp(`/contracts/${lineItemContractLabel(openItem)}`);
        }}
      />
    );

  /**
   * C29 · the period detail, in whichever frame.
   *
   * ⚠️ **The contract comes from the ADDRESS as well as from what is open**, because the period
   * page opens no contract — it names one. Same shape as the worklog read above it.
   */
  const periodContract =
    contracts.find((candidate) => contractMatches(candidate, openCode ?? params.code)) ?? null;
  const periodList =
    periodContract === null
      ? []
      : occurrences(periodContract.recurrence, periodContract.endsOn, toWireDate(new Date()));
  const period = periodList.find((one) => one.from === openPeriod) ?? null;
  /* 31 Oct 2026 · the open period's logs, paged on the server. */
  const periodLogs = usePeriodLogs(session, periodContract?.id ?? null, period, liveNonce);

  /**
   * C55/C57 · ⚠️ **One read for the WHOLE series, when a period is open.** `forContract` derives
   * every period in a single pass precisely so a screen does not ask per period — asking here for
   * the one on screen would throw that away the moment somebody steps to the next month.
   *
   * ⚠️ **And when a RECURRING contract is open, for its *Időszakok* table** (ux-pass-2, 14 Oct) —
   * the same series, so stepping from the contract into one of its periods re-reads nothing. A
   * refusal leaves `[]`, which hides the table's money columns rather than drawing zeros.
   */
  const periodContractId =
    period !== null
      ? (periodContract?.id ?? null)
      : open !== null && open.recurrence !== null
        ? open.id
        : null;
  useEffect(() => {
    setPeriodMoney([]);
    if (periodContractId === null || session.workspaceId === null) return;
    void (async () => {
      const response = await session
        .request(`/contracts/${periodContractId}/economics`)
        .catch(() => null);
      if (response?.ok !== true) return;
      setPeriodMoney(parseList(occurrenceEconomicsSchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [periodContractId, session.workspaceId, moneyNonce]);

  /**
   * 30 Oct 2026 · the collector task of each recurring contract on screen, asked for by name.
   * Non-fatal: without one the card and the period simply name no collector.
   */
  const collectorFor = [
    open !== null && open.recurrence !== null ? open.id : null,
    periodContract !== null && periodContract.recurrence !== null ? periodContract.id : null,
  ]
    .filter((id): id is string => id !== null)
    .filter((id, index, all) => all.indexOf(id) === index)
    .join(',');
  useEffect(() => {
    if (collectorFor === '' || session.workspaceId === null) {
      setTasks([]);
      return;
    }
    void (async () => {
      const lists = await Promise.all(
        collectorFor
          .split(',')
          .map((id) =>
            readAll(
              session,
              `/tasks?recurringContractId=${encodeURIComponent(id)}`,
              taskSchema,
            ).catch(() => null),
          ),
      );
      setTasks(lists.flatMap((list) => list ?? []));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [collectorFor, session.workspaceId, liveNonce]);

  /**
   * 30 Oct 2026 · a task named by id, opened by its key. Found among what the screen holds, else
   * asked for — a worklog's task need not be on the open line item's project.
   */
  function openTaskById(taskId: string): void {
    const held = [...tasks, ...lineItemTasks.rows].find((candidate) => candidate.id === taskId);
    if (held !== undefined) {
      go(`/tasks/${held.key}`);
      return;
    }
    void (async () => {
      const response = await session.request(`/tasks/${taskId}`).catch(() => null);
      if (response?.ok !== true) return;
      const parsed = taskSchema.safeParse(await response.json().catch(() => null));
      if (parsed.success) go(`/tasks/${parsed.data.key}`);
    })();
  }

  /* 17 Oct 2026 · ⚠️ Non-fatal like the period money: a refusal leaves `[]`, and the column says –. */
  const openContractId = open?.id ?? null;
  useEffect(() => {
    setLineItemMoney([]);
    if (openContractId === null || session.workspaceId === null) return;
    void (async () => {
      const response = await session
        .request(`/contracts/${openContractId}/line-items/economics`)
        .catch(() => null);
      if (response?.ok !== true) return;
      setLineItemMoney(parseList(lineItemEconomicsSchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug). */
  }, [openContractId, session.workspaceId, moneyNonce]);

  /**
   * ⚠️ **What is left to bill on the source the dialog was opened from**, formatted. The server
   * refuses anything larger, and a refusal arriving after the form is filled in is the worst place
   * to learn a number that was knowable all along.
   */
  function billingRemaining(draft: InvoiceDraft): string {
    const remaining =
      draft.targetKind === 'line_item'
        ? economics?.remaining
        : periodMoney.find((one) => one.startsOn === draft.occurrenceStartsOn)?.remaining;
    if (remaining === undefined) return '';
    const symbol =
      currencies.find((one) => one.code === draft.currencyCode)?.symbol ?? draft.currencyCode;
    return formatMoney(Number(remaining), { code: draft.currencyCode, symbol });
  }

  /**
   * C57 · ⚠️ **One dialog, rendered on every branch below.** The line item's *Számlázás* and the
   * period's open it, and both of those surfaces appear on all four routes — a dialog mounted in
   * only one of them is the bug the line-item dialog above already had once.
   */
  const billingDialog =
    billing === null ? null : (
      <InvoiceDialog
        draft={billing}
        lineItems={invoiceData.lineItems}
        contracts={invoiceData.contracts}
        /* ⚠️ Empty, and that is not a gap: the dialog is only ever opened LOCKED from a source, so
           the contract and period pickers are hidden and have nothing to offer. */
        periods={[]}
        members={metafieldMembers}
        remainingLabel={billingRemaining(billing)}
        serverError={invoiceData.serverError}
        onCancel={() => {
          setBilling(null);
          invoiceData.clearError();
        }}
        onSave={(body, closeSource) => {
          void (async () => {
            const saved = await invoiceData.save(billing.id, body, closeSource);
            if (saved !== null) {
              setBilling(null);
              /* ⚠️ The source's own figures move with the invoice — *Kiszámlázva* is part of them —
                 so the economics are re-read rather than left one invoice behind. */
              setMoneyNonce((count) => count + 1);
              await reload();
            }
          })();
        }}
      />
    );

  const periodSurface =
    periodContract === null || period === null ? null : (
      <PeriodSurface
        mode={periodMode}
        open
        data={{
          contract: periodContract,
          lineItems: lineItems.filter((item) => item.contractId === periodContract.id),
          period,
          total: periodList.length,
          logs: periodLogs,
          today: toWireDate(new Date()),
          collector:
            tasks
              .filter((task) => task.recurringContractId === periodContract.id)
              .map((task) => ({ id: task.id, key: task.key }))[0] ?? null,
          economics: periodMoney.find((one) => one.startsOn === period.from) ?? null,
          /**
           * C57 · ⚠️⚠️ **THE MOCKUP'S `occInvoices`, AND BOTH ARMS MATTER.** One named at this
           * occurrence counts against it wherever it was dated; one named at a LINE ITEM of the
           * same contract counts against the period its date falls in. Listing only the first would
           * make a deliverable billed mid-frame vanish from every period of the contract.
           */
          invoices: invoiceData.invoices.filter((one) =>
            one.targetKind === 'occurrence'
              ? one.contractId === periodContract.id && one.occurrenceStartsOn === period.from
              : one.contractIdentifier === periodContract.identifier &&
                one.issuedOn >= period.from &&
                one.issuedOn <= period.to,
          ),
          currencies,
        }}
        handlers={{
          /* 22 Oct 2026 · the server's answer for this contract's project (`project-reach.ts`). */
          canManage: periodContract.canManage,
          onOpenContract: () => {
            setOpenPeriod(null);
            go(`/contracts/${contractLabel(periodContract)}`);
          },
          onOpenProject: () => {
            if (periodContract.projectId !== null) go(`/projects/${periodContract.projectId}`);
          },
          onOpenTask: openTaskById,
          /* ⚠️ Stepping re-addresses when the page is the frame, so the arrows are shareable too —
             REPLACING, so the back arrow leaves the period rather than walking back through
             every month stepped past. */
          onStep: (next) => {
            const target = periodList.find((one) => one.index === next.index);
            if (target === undefined) return;
            if (periodPage) {
              go(`/contracts/${contractLabel(periodContract)}/periods/${target.from}`, {
                replace: true,
              });
            } else setOpenPeriod(target.from);
          },
          onSetOpen: (nextOpen) => {
            void write(`/contracts/${periodContract.id}/periods`, {
              method: 'PUT',
              body: JSON.stringify({ startsOn: period.from, open: nextOpen }),
            });
          },
          ...(periodContract.canManage
            ? {
                onBill: () => {
                  const money = periodMoney.find((one) => one.startsOn === period.from) ?? null;
                  setBilling(
                    emptyInvoice(
                      money?.currencyCode ?? baseCurrencyOf(currencies)?.code ?? 'HUF',
                      '',
                      {
                        targetKind: 'occurrence',
                        contractId: periodContract.id,
                        occurrenceStartsOn: period.from,
                        amount: money?.remaining ?? '',
                        locked: true,
                      },
                    ),
                  );
                },
              }
            : {}),
          onOpenInvoice: (invoice: Invoice) => {
            go(`/invoices/${invoice.identifier}`);
          },
          /* 3 Nov 2026 · a file onto each invoice row (the product owner: per invoice is enough),
             re-reading the list so the row's count moves. */
          ...(periodContract.canManage
            ? {
                onAttachInvoice: async (invoice: Invoice, file: File) => {
                  const code = await attachToInvoice(
                    (path, init) => session.request(path, init),
                    invoice.id,
                    file,
                  );
                  if (code === null) await invoiceData.reload();
                  return code;
                },
              }
            : {}),
          /**
           * ⚠️⚠️ **THE EXISTING WORKLOG EXPORT, NARROWED.** A period has no id to export by — but it
           * has two dates and a contract, and that is exactly its rows. A second export endpoint
           * would have been a second CSV writer, a second permission check and a second row cap.
           */
          onExport: () => {
            exporter.open('worklog', {
              from: period.from,
              to: period.to,
              contractId: periodContract.id,
            });
          },
          ...(periodPage
            ? {}
            : {
                onExpand: () => {
                  go(`/contracts/${contractLabel(periodContract)}/periods/${period.from}`);
                },
              }),
        }}
        onModeChange={(next) => {
          if (next === 'full') {
            go(`/contracts/${contractLabel(periodContract)}/periods/${period.from}`);
            return;
          }
          if (periodPage) go(`/contracts/${contractLabel(periodContract)}`);
          setPeriodMode(next);
        }}
        onClose={() => {
          setOpenPeriod(null);
          /* ⚠️ Back to where the period was opened from, else up to its contract (R-61). */
          if (periodPage) {
            goUp(`/contracts/${contractLabel(periodContract)}`, { followOrigin: true });
          }
        }}
      />
    );

  /**
   * ⚠️⚠️ **NOT DRAWN BEHIND A FULL-PAGE LINE ITEM.** `openCode` is state and the address is not, so
   * arriving at `/contracts/:code/:number` left the contract surface mounted underneath — which is
   * the other half of the split screen above. The ladder says a full page REPLACES what it grew
   * out of; this is that rule stated where the surface is drawn, so no navigation can forget it.
   *
   * ⚠️ A line item at `drawer` or `modal` still draws over its contract, which is the ladder
   * working rather than an exception to it.
   */
  const surface =
    open === null || (openItemId !== null && itemMode === 'full') ? null : (
      <ContractSurface
        mode={mode}
        open
        /* ⚠️ R-38 · workspace-relative, so `go` supplies the first segment. */
        onNavigate={(path) => {
          go(path);
        }}
        data={{
          contract: open,
          lineItems: lineItems.filter((item) => item.contractId === open.id),
          worklogs,
          metafields: contractFields,
          members: metafieldMembers,
          periodMoney,
          lineItemMoney,
          currencies,
          /* 30 Oct 2026 · read by `recurringContractId` above (`collectorFor`). */
          collector:
            tasks
              .filter((task) => task.recurringContractId === open.id)
              .map((task) => ({ id: task.id, key: task.key, title: task.title }))[0] ?? null,
        }}
        handlers={{
          /* 22 Oct 2026 · the server's answers for this contract's project (`project-reach.ts`). */
          canManage: open.canManage,
          canManageLineItems: open.canManageLineItems,
          /* ⚠️ The tier, not a permission: `contracts.line_item_pricing` decides whether the type
           * is editable at all, and the sidebar read is where the resolved flags arrive. */
          pricing: sidebar?.view.features['contracts.line_item_pricing'] === true,
          onPatch: (patch) => {
            void write(`/contracts/${open.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ ...patch, version: open.version }),
            });
          },
          /**
           * C56 · ⚠️⚠️ **ITS OWN VERB, AND THE REFUSAL CARRIES THREE FIGURES.** `write`
           * stores the code and `messageFor` turns `contract_still_bound` into the sentence the
           * mockup writes — how many tasks, how many hours, and which project — because the
           * reader's next move is to close or move them.
           */
          onDetachProject: () => {
            void write(`/contracts/${open.id}/project`, {
              method: 'DELETE',
              body: JSON.stringify({ version: open.version }),
            });
          },
          onNewLineItem: () => {
            setItemDialogOpen(true);
          },
          onPatchLineItem: (item, patch) => {
            void write(`/line-items/${item.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ ...patch, version: item.version }),
            });
          },
          /* ⚠️ **No confirmation dialog, because the destructive case cannot happen**: the menu
             disables Delete while any task points at the line item, and `line_item_in_use` refuses
             it on the server anyway. What is left deletes a row with nothing on it. */
          onDeleteLineItem: (item) => {
            void write(`/line-items/${item.id}`, { method: 'DELETE' });
          },
          onOpenLineItem: (item) => {
            /* ⚠️ **From the CONTRACT's surface** — a line item opened from a contract drawer stays
               a drawer; from a contract modal it commits to the full page. `nextSurface`'s note on
               that escalation is the one worth reading. */
            const next = nextSurface(mode);

            /**
             * ⚠️⚠️ **AN ESCALATION TO `full` IS A NAVIGATION, AND SKIPPING THAT SPLIT THE SCREEN
             * IN TWO.** `onModeChange` below already knew this — *"growing to full is a NAVIGATION,
             * so the address matches what is on screen"* — and this path set the mode without
             * moving. So the line item rendered as a page while `openCode` still held the contract,
             * and both surfaces drew at once: the contract modal on top, the line-item page beneath
             * it, with the contract list showing through. Reported exactly that way.
             */
            if (next === 'full') {
              go(`/contracts/${lineItemContractLabel(item)}/${item.identifier}`);
              return;
            }

            setOpenItemId(item.id);
            setItemMode(next);
          },
          /**
           * ⚠️ **Resolves to a CODE rather than throwing**, because the card beside the button
           * writes a different sentence for each of C22's four refusals — an infected file is the
           * uploader's problem and permanent, an unreachable scanner is an operator's and
           * temporary, and telling somebody their signed PDF is a virus because clamd was
           * restarting is the worst of both.
           */
          onAttach: async (file) => {
            const form = new FormData();
            form.set('file', file);
            const response = await session
              .request(`/files?target=contract&targetId=${open.id}`, {
                method: 'POST',
                body: form,
              })
              .catch(() => null);
            if (response?.ok === true) {
              await reload();
              return null;
            }
            const envelope = (await response?.json().catch(() => null)) as
              { error?: { code?: string } } | null | undefined;
            return envelope?.error?.code ?? 'unavailable';
          },
          onOpenProject: () => {
            if (open.projectId !== null) go(`/projects/${open.projectId}`);
          },
          onMetafields: (values) => {
            void write(`/contracts/${open.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ metafields: values, version: open.version }),
            });
          },
          onOpenTask: openTaskById,
          /* ⚠️ A DRAWER over the contract, not a navigation — the mockup's own `openOcc`, and the
             reason the period has a drawer at all: reading one month is a glance, not a trip. */
          /**
           * ⚠️⚠️ **ESCALATING TO THE FULL PAGE IS A NAVIGATION, AND SETTING THE MODE WAS NOT.**
           * `nextSurface('modal')` is `'full'` — a modal has no room for a detail beside its own
           * content, so drilling in from one commits to the page. This set the period's mode to
           * `full` and left the contract modal open on top of it, which rendered the period screen
           * *underneath* the modal at the bottom of the list: the surface existed, was the right
           * one, and was unreachable. `onModeChange` below already navigates for exactly this
           * case; the opener had its own copy of the decision and only did half of it.
           */
          onOpenPeriod: (occurrence) => {
            const next = nextSurface(mode);
            if (next === 'full') {
              go(`/contracts/${contractLabel(open)}/periods/${occurrence.from}`);
              return;
            }
            /* ⚠️ From a drawer it stays a drawer — reading one month is a glance, which is what
               the mockup's own `openOcc` does. */
            setOpenPeriod(occurrence.from);
            setPeriodMode(next);
          },
          /* 17 Oct 2026 · a period row's menu — the phase 2 mockup's `occMenu`. */
          periodMenu: {
            onOpenFull: (occurrence) => {
              go(`/contracts/${contractLabel(open)}/periods/${occurrence.from}`);
            },
            onExport: (occurrence, format) => {
              exporter.run({
                domain: 'worklog',
                format,
                from: occurrence.from,
                to: occurrence.to,
                contractId: open.id,
                reportId: null,
                projectId: null,
                ids: null,
                memberId: null,
              });
            },
            onOpenLogs: (occurrence) => {
              const query = new URLSearchParams({
                from: occurrence.from,
                to: occurrence.to,
                contractId: open.id,
                /* 3 Nov 2026 · the chip READS the contract's name, not its identifier. */
                contract: open.name,
              });
              go(`/time/logs?${query.toString()}`);
            },
            onCopy: (text) => {
              /* ⚠️ Widened: `navigator.clipboard` is typed as present and is absent on an insecure
                 origin. A failed copy says nothing rather than claiming one. */
              const clipboard = (
                navigator as unknown as {
                  clipboard?: { writeText?: (value: string) => Promise<void> };
                }
              ).clipboard;
              void clipboard
                ?.writeText?.(text)
                .then(() => {
                  setSyncToasts((current) => [
                    ...current,
                    {
                      id: `period-copy-${String(Date.now())}`,
                      title: t('period.menu.copied'),
                      description: text,
                      variant: 'info' as const,
                    },
                  ]);
                })
                .catch(() => undefined);
            },
            ...(open.canManage
              ? {
                  onSetOpen: (occurrence: Occurrence, nextOpen: boolean) => {
                    void write(`/contracts/${open.id}/periods`, {
                      method: 'PUT',
                      body: JSON.stringify({ startsOn: occurrence.from, open: nextOpen }),
                    });
                  },
                }
              : {}),
          },
          ...(mode === 'full'
            ? {}
            : {
                onExpand: () => {
                  go(`/contracts/${contractLabel(open)}`);
                },
              }),
        }}
        onModeChange={(next) => {
          /* ⚠️ Growing to full is a NAVIGATION, so the address matches what is on screen and the
           * page is shareable; shrinking comes back to the list with the drawer still open. */
          if (next === 'full') go(`/contracts/${contractLabel(open)}`);
          else if (page) go('/contracts');
          setMode(next);
        }}
        onClose={close}
      />
    );

  /**
   * ⚠⚠️ **THE PAGE CARRIES ITS OWN MARKER EVEN WHEN THE CONTRACT IS UNKNOWN, AND THE ROUTE
   * GATE IS WHAT FOUND THIS.** `/contracts/HSO-C01` for a code this workspace does not have rendered
   * the LIST's marker, because the surface only exists when a contract matched — so a bookmark to a
   * deleted contract silently became the list, at an address that says otherwise. A page that cannot
   * find its subject says so, in place, and stays the page.
   */
  /**
   * P2-9 · `/contracts/:code/:number`.
   *
   * ⚠️ **Its own marker, and the same lesson the contract page learned**: an ordinal this contract
   * does not have must not silently render the contract's page — the address says line item, so the
   * page stays a line item and says it cannot find that one.
   */
  /**
   * P2-14 · `/contracts/:code/periods/:from`.
   *
   * ⚠️ **Its own marker and its own not-found sentence**, the lesson the contract page learned: a
   * date this recurrence does not start a period on must not render the contract's page at an
   * address that says period.
   */
  /**
   * `0161` · the answer a *Szinkronizálás most* press gets.
   *
   * ⚠️⚠️ **DEFINED ONCE AND RENDERED BY EVERY RETURN BELOW**, like `billingDialog`. The line item
   * page, the period page and the contract page each have their own return, and the first version
   * of this lived only in the list's — so the button on the line item PAGE, the one place it is
   * pressed most, answered with nothing at all.
   */
  const syncToaster = (
    <>
      {exporter.dialog}
      {costDraft === null ? null : (
        <ExternalCostDialog
          draft={costDraft}
          currencies={currencies}
          lineItems={lineItems
            .filter((item) => item.id === costDraft.lineItemId)
            .map((item) => ({ id: item.id, name: item.name }))}
          contracts={[]}
          periods={[]}
          members={metafieldMembers}
          serverError={costError}
          onCancel={() => {
            setCostDraft(null);
          }}
          onSave={(body) => {
            void (async () => {
              const response = await session
                .request('/external-costs', { method: 'POST', body: JSON.stringify(body) })
                .catch(() => null);
              if (response?.ok !== true) {
                setCostError(await ownerOrLineItemRefusal(t, response));
                return;
              }
              setCostDraft(null);
              /* The card and the money box both read costs — re-read them together. */
              setMoneyNonce((count) => count + 1);
            })();
          }}
        />
      )}
      <Toaster
        toasts={[...syncToasts, ...exporter.toasts]}
        label={t('projects.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setSyncToasts((current) => current.filter((entry) => entry.id !== id));
          exporter.dismiss(id);
        }}
      />
    </>
  );

  if (periodPage) {
    return (
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <div data-screen="period" className="flex min-h-0 flex-1 flex-col">
          {periodSurface ?? (
            <p className="text-muted-foreground p-5 text-[12.5px]">{t('period.unknown')}</p>
          )}
        </div>
        {billingDialog}
        {syncToaster}
      </ScreenState>
    );
  }

  if (itemPage) {
    return (
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <div data-screen="lineitem" className="flex min-h-0 flex-1 flex-col">
          {itemSurface ?? (
            <p className="text-muted-foreground p-5 text-[12.5px]">{t('lineItem.unknown')}</p>
          )}
        </div>
        {billingDialog}
        {syncToaster}
      </ScreenState>
    );
  }

  if (page) {
    return (
      <ScreenState failure={failure} onRetry={() => void reload()}>
        <div data-screen="contract" className="flex min-h-0 flex-1 flex-col">
          {surface ?? (
            <p className="text-muted-foreground p-5 text-[12.5px]">{t('contract.unknown')}</p>
          )}
          {/* ⚠️ **The page needs the line-item surface too, and shipped without it.** The contract
              detail is the same component on both routes, so its line-item rows opened nothing at
              `/contracts/HSO-C01` — the click set state that only the LIST route rendered. */}
          {itemSurface}
          {periodSurface}
        </div>
        {/* ⚠️ **Rendered on BOTH the list and the page**, because the line-item dialog belongs to the
          contract detail and the contract detail is on both. `open === null` is what keeps it out
          of the way when nothing is open. */}
        {open === null || !itemDialogOpen ? null : (
          <LineItemDialog
            open
            contract={open}
            pricing={
              /* 22 Oct 2026 · no price fields for a reader without the project's money. */
              sidebar?.view.features['contracts.line_item_pricing'] === true && open.moneyVisible
            }
            workFreeRevenue={sidebar?.view.features['contracts.work_free_revenue'] === true}
            currencies={currencies}
            metafields={itemFields}
            members={metafieldMembers}
            serverError={
              serverError === null
                ? null
                : t(contractErrorKey(serverError.code), serverError.values)
            }
            onClose={() => {
              setItemDialogOpen(false);
            }}
            onSubmit={saveLineItem}
          />
        )}
        {billingDialog}
        {syncToaster}
      </ScreenState>
    );
  }

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      {/* ⚠️ No `lineItems` and no mode — item 24: a line item opens from the contract that owns
          it, and the flat list is gone. `contracts-screen.tsx` carries the argument. */}
      <ContractsScreen
        nonce={liveNonce}
        onRows={setListed}

        serverError={serverError}
        onOpen={(contract) => {
          /* ⚠️ C56 · the label, not the code — a client-scoped contract opens by its identifier,
               and `contractMatches` accepts either. Passing the raw code would set null here and
               the drawer would simply never open. */
          setOpenCode(contractLabel(contract));
          setMode(nextSurface('page'));
        }}
        {...(permissions.has('contract.manage')
          ? {
              onNew: () => {
                setNewOpen(true);
              },
              /* The selection's status — `bulk-status.ts`; its toast joins the route's own. */
              onBulkStatus: (ids, status) => {
                void (async () => {
                  const toast = await bulkContractStatus(
                    session,
                    contracts,
                    ids,
                    status,
                    reload,
                    t,
                  );
                  setSyncToasts((current) => [...current, toast]);
                })();
              },
            }
          : {})}
      />
      {itemSurface}
      {periodSurface}
      {surface}

      {/* ⚠️ **Only for somebody who can `contract.manage`** — `onNew` is absent otherwise, and the
          screen draws neither the toolbar button nor the empty state's. UI gating is UX; the
          server refuses either way (rule 6). */}
      {permissions.has('contract.manage') ? (
        <ContractDialog
          open={newOpen}
          projects={projects}
          clients={clients}
          /* ⚠️ C55's row gates both halves of one capability — see the dialog's own note. */
          workFreeRevenue={sidebar?.view.features['contracts.work_free_revenue'] === true}
          metafields={contractFields}
          members={metafieldMembers}
          serverError={
            serverError === null ? null : t(contractErrorKey(serverError.code), serverError.values)
          }
          onClose={() => {
            setNewOpen(false);
          }}
          onSubmit={createContract}
        />
      ) : null}
      {/* ⚠️ **Rendered on BOTH the list and the page**, because the line-item dialog belongs to the
          contract detail and the contract detail is on both. `open === null` is what keeps it out
          of the way when nothing is open. */}
      {open === null || !itemDialogOpen ? null : (
        <LineItemDialog
          open
          contract={open}
          pricing={
            /* 22 Oct 2026 · no price fields for a reader without the project's money. */
            sidebar?.view.features['contracts.line_item_pricing'] === true && open.moneyVisible
          }
          workFreeRevenue={sidebar?.view.features['contracts.work_free_revenue'] === true}
          currencies={currencies}
          metafields={itemFields}
          members={metafieldMembers}
          serverError={
            serverError === null ? null : t(contractErrorKey(serverError.code), serverError.values)
          }
          onClose={() => {
            setItemDialogOpen(false);
          }}
          onSubmit={saveLineItem}
        />
      )}
      {billingDialog}
      {syncToaster}
    </ScreenState>
  );
}

/** `/contracts/:code` — the same route, opened straight into the page frame. */
export function ContractPageRoute() {
  return <ContractsRoute page />;
}

/** `/contracts/:code/:number` — the line item's own page (P2-9). */
export function LineItemPageRoute() {
  return <ContractsRoute itemPage />;
}

/** `/contracts/:code/periods/:from` — one derived period, at an address (P2-14). */
export function PeriodPageRoute() {
  return <ContractsRoute periodPage />;
}
